import axios, { type AxiosError } from 'axios';
import { QueryClient, type Query, type QueryFunctionContext } from 'react-query';
import { get, set } from 'idb-keyval';
import { reportError } from 'src/modules/logs/Sentry';
import { getBackendUrl } from 'src/config/backend';
import { getAppVersion } from 'src/modules/appVersion';
import { debounce } from 'src/utils/debounce';
import { isCoreQueryKey, isPersistedQueryKey } from 'src/queries/persistedQueryKeys';

export const defaultQueryFn = async ({ queryKey }: QueryFunctionContext) => {
  const { data } = await axios.get(
    `${getBackendUrl()}/api/${queryKey[0] as string}${queryKey[1] ? `/${queryKey[1] as string | number}` : ''}`,
  );
  return data;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 60 * 24 * 7,

      retry: (failureCount: number, error) => {
        const axiosError = error as AxiosError;
        const status = axiosError?.response?.status;

        if (status && status >= 400 && status < 500) {
          return false;
        }

        if (!axiosError?.response) {
          return failureCount < 5;
        }

        if (status && status >= 500) {
          return failureCount < 3;
        }

        return false;
      },

      retryDelay: (attemptIndex: number) => {
        return Math.min(1000 * 2 ** attemptIndex, 30000);
      },

      refetchOnReconnect: 'always',
    },

    mutations: {
      retry: (failureCount: number, error) => {
        const axiosError = error as AxiosError;

        if (axiosError?.response) {
          return false;
        }

        return failureCount < 2;
      },

      retryDelay: (attemptIndex: number) => {
        return 500 * 2 ** attemptIndex;
      },
    },
  },
});

export const CACHE_KEY = 'baobab-query-cache';
export const CACHE_VERSION_KEY = 'baobab-cache-version';
const MAX_PERSISTED_BYTES = 2 * 1024 * 1024;
const MAX_PERSISTED_QUERIES = 100;

let isRestoring = false;

// Some IndexedDB failures are transient, environmental, and unactionable: iOS Safari / WKWebView
// routinely drops the IDB connection (and Firefox raises AbortError under storage pressure /
// private mode). The cache is best-effort offline support — it re-persists on the next change and
// re-restores on the next boot — so escalating these to Sentry only generates noise.
export const isTransientIdbError = (error: unknown): boolean => {
  const name = (error as { name?: unknown } | null)?.name;
  const message = (error as { message?: unknown } | null)?.message;
  const text = `${typeof name === 'string' ? name : ''} ${
    typeof message === 'string' ? message : ''
  }`.toLowerCase();

  return (
    text.includes('connection to indexed database server lost') ||
    text.includes('connection to the indexed database server lost') ||
    text.includes('unknownerror') ||
    text.includes('aborterror') ||
    text.includes('the database connection is closing') ||
    text.includes('an attempt was made to use an object that is not')
  );
};

const reportCacheError = (message: string, error: unknown) => {
  if (isTransientIdbError(error)) {
    console.warn(`${message} (transient IndexedDB error, ignored)`, error);
    return;
  }
  reportError(message, error);
};

export const restoreCache = async () => {
  try {
    const cachedVersion = await get(CACHE_VERSION_KEY);
    // Read LAZILY, here — after a real await — not at module top level (the retired
    // `packageJson.version` was). base.ts is imported (transitively) by nearly everything,
    // including src/index.tsx itself, and that import is fully evaluated BEFORE index.tsx's own
    // top-level code (including its `__APP_VERSION__` seed) runs — plain ES module evaluation
    // order. A top-level getAppVersion() would read 'unknown'; the await below yields until the seed ran.
    const currentVersion = getAppVersion();

    if (cachedVersion !== currentVersion) {
      await set(CACHE_VERSION_KEY, currentVersion);
      return;
    }

    const cacheSnapshot = await get(CACHE_KEY);
    if (Array.isArray(cacheSnapshot)) {
      isRestoring = true;
      cacheSnapshot.forEach(({ queryKey, state }) => {
        if (!isPersistedQueryKey(queryKey)) {
          return;
        }
        // Race: this restore is async (IndexedDB read) and the persist side is debounced 5s, so the
        // snapshot can predate a mutation. On reload, the mount fetch and this IDB read can resolve
        // in EITHER order — comparing timestamps is not enough to make restore safe: a query that
        // has already been created by a mounted observer but whose fetch hasn't resolved yet
        // reports `dataUpdatedAt: 0` (react-query's default state for a query with no data), which
        // is falsy and used to defeat a `>=` timestamp comparison, letting a stale snapshot
        // overwrite it right before the live fetch's response arrives.
        // Invariant: restore only into a slot with NO existing query state; live state always wins.
        if (queryClient.getQueryState(queryKey)) {
          return;
        }
        queryClient.setQueryData(queryKey, state.data, {
          updatedAt: state.dataUpdatedAt,
        });
      });
    }
  } catch (error) {
    reportCacheError('Failed to restore cache', error);
  } finally {
    isRestoring = false;
  }
};

const buildCacheSnapshot = (queries: Query[], now: number) => {
  const candidates = queries
    .filter(query => query.state.status === 'success')
    .filter(query => isPersistedQueryKey(query.queryKey))
    .filter(query => {
      const age = now - query.state.dataUpdatedAt;
      const maxAge = 7 * 24 * 60 * 60 * 1000;
      return age < maxAge;
    })
    .sort(
      (a, b) =>
        Number(isCoreQueryKey(b.queryKey)) - Number(isCoreQueryKey(a.queryKey)) ||
        b.state.dataUpdatedAt - a.state.dataUpdatedAt,
    )
    .slice(0, MAX_PERSISTED_QUERIES);

  let totalBytes = 0;
  return candidates.flatMap(query => {
    const entry = {
      queryKey: query.queryKey,
      state: {
        data: query.state.data,
        dataUpdatedAt: query.state.dataUpdatedAt,
      },
    };
    const entryBytes = new TextEncoder().encode(JSON.stringify(entry)).length;
    if (totalBytes + entryBytes > MAX_PERSISTED_BYTES) {
      return [];
    }
    totalBytes += entryBytes;
    return [entry];
  });
};

export const persistCache = () => {
  try {
    const cacheSnapshot = buildCacheSnapshot(
      queryClient.getQueryCache().getAll(),
      Date.now(),
    );

    set(CACHE_KEY, cacheSnapshot).catch(error => {
      reportCacheError('Failed to persist cache', error);
    });
  } catch (error) {
    reportCacheError('Failed to persist cache', error);
  }
};

restoreCache();

const persistCacheDebounced = debounce(persistCache, 5000);
queryClient.getQueryCache().subscribe(event => {
  if (isRestoring || !event) return;
  const isSuccess =
    event.type === 'queryUpdated' && event.action.type === 'success';
  const isRemoval =
    event.type === 'queryRemoved' && isPersistedQueryKey(event.query.queryKey);
  if (isSuccess || isRemoval) persistCacheDebounced();
});
